import React from "react";

/**
 * @typedef {Object} TabItemProps
 * @property {string} label The label associated with the item.
 * @property {string} [name] Optional unique name for tab matching, defaults to label.
 */

/**
 * @param {object} props
 * @param {React.ReactElement<TabItemProps>[]} props.children
 * @param {string} [props.className]
 * @param {string} [props.controlledActiveTab]
 * @param {(tab: string) => void} [props.setControlledActiveTab]
 * @returns {React.Component}
 */
const TabList = ({
  children,
  className,
  controlledActiveTab,
  setControlledActiveTab,
}) => {
  const [activeTab, setActiveTab] = [
    controlledActiveTab,
    setControlledActiveTab,
  ];

  /**
   * @param {Event} e - The click event.
   * @param {string} newActiveTab - The new active tab to be set.
   */
  const handleClick = (e, newActiveTab) => {
    e.preventDefault();
    setActiveTab(newActiveTab);
  };
  const numChildren = React.Children.count(children);

  return (
    <div className={`flex flex-col rounded-lg ${className ? className : ""}`}>
      <div className="flex bg-c-gray-600 rounded-tl-lg rounded-tr-lg">
        {children.map((child, idx) => {
          const tabName = child.props.name || child.props.label;
          return (
            <button
              key={tabName}
              className={`${activeTab === tabName ? "bg-accent hover:bg-accent-hover" : "hover:border-b-c-gray-500 hover:bg-c-gray-500 border-b border-b-c-gray-500"} flex-1 font-medium py-1.5 ${idx === 0 ? "rounded-tl-lg" : ""} ${idx === numChildren - 1 ? "rounded-tr-lg" : ""}`}
              onClick={(e) => handleClick(e, tabName)}
            >
              {child.props.label}
            </button>
          );
        })}
      </div>
      {children.map((child) => {
        const tabName = child.props.name || child.props.label;
        return (
          tabName === activeTab && (
            <div key={tabName} className="max-h-full overflow-y-scroll">
              {child.props.children}
            </div>
          )
        );
      })}
    </div>
  );
};

/**
 * @param {object} props
 * @param {string} props.label
 * @param {string} [props.name]
 * @param {import('react').ReactElement<any, any>} props.children
 * @returns {React.Component}
 */
const Tab = ({ label, name, children }) => {
  return (
    <div label={label} name={name} className="overflow-y-automax-h-96">
      {children}
    </div>
  );
};
export { TabList, Tab };
